/**
 * Custom Hook for Notifications Management
 */

import { useState, useEffect, useCallback } from 'react';
import {
  notificationsService,
  Notification,
  NotificationsQuery,
} from '../services/notifications.service';

interface UseNotificationsState {
  notifications: Notification[];
  total: number;
  unreadCount: number;
  page: number;
  limit: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
}

export function useNotifications(initialQuery?: NotificationsQuery) {
  const [state, setState] = useState<UseNotificationsState>({
    notifications: [],
    total: 0,
    unreadCount: 0,
    page: 1,
    limit: 20,
    totalPages: 0,
    loading: true,
    error: null,
  });

  const [query, setQuery] = useState<NotificationsQuery>(initialQuery || { page: 1, limit: 20 });

  const fetchNotifications = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const response = await notificationsService.getNotifications(query);
      if (response.success && response.data) {
        const payload: any = response.data;
        const firstDataLayer = payload?.data;
        const secondDataLayer = firstDataLayer?.data;

        const rows: Notification[] = Array.isArray(payload)
          ? payload
          : Array.isArray(firstDataLayer)
            ? firstDataLayer
            : Array.isArray(secondDataLayer)
              ? secondDataLayer
              : [];

        const meta =
          payload?.meta ||
          firstDataLayer?.meta ||
          secondDataLayer?.meta ||
          {};

        const unreadCount = Number(
          meta?.unreadCount ??
          payload?.unreadCount ??
          firstDataLayer?.unreadCount ??
          rows.filter((n) => !n.isRead).length,
        );

        setState((prev) => ({
          ...prev,
          notifications: rows,
          total: Number(meta?.total ?? payload?.total ?? firstDataLayer?.total ?? rows.length),
          unreadCount,
          page: Number(meta?.page ?? payload?.page ?? firstDataLayer?.page ?? 1),
          limit: Number(meta?.limit ?? payload?.limit ?? firstDataLayer?.limit ?? (rows.length || 1)),
          totalPages: Number(meta?.totalPages ?? payload?.totalPages ?? firstDataLayer?.totalPages ?? 1),
          loading: false,
        }));
      }
    } catch (error: any) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || 'Failed to load notifications',
      }));
    }
  }, [query]);

  const markAsRead = useCallback(async (id: string) => {
    try {
      await notificationsService.markAsRead(id);
      await fetchNotifications();
    } catch (error: any) {
      console.error('Failed to mark notification as read:', error);
      throw error;
    }
  }, [fetchNotifications]);

  const markAllAsRead = useCallback(async () => {
    try {
      await notificationsService.markAllAsRead();
      await fetchNotifications();
    } catch (error: any) {
      console.error('Failed to mark all as read:', error);
      throw error;
    }
  }, [fetchNotifications]);

  const deleteNotification = useCallback(async (id: string) => {
    try {
      await notificationsService.deleteNotification(id);
      await fetchNotifications();
    } catch (error: any) {
      console.error('Failed to delete notification:', error);
      throw error;
    }
  }, [fetchNotifications]);

  const updateQuery = useCallback((newQuery: Partial<NotificationsQuery>) => {
    setQuery((prev) => ({ ...prev, ...newQuery }));
  }, []);

  const refresh = useCallback(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  return {
    notifications: state.notifications,
    total: state.total,
    unreadCount: state.unreadCount,
    page: state.page,
    limit: state.limit,
    totalPages: state.totalPages,
    loading: state.loading,
    error: state.error,
    query,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    updateQuery,
    refresh,
  };
}
